import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { isNative, takePhoto } from "@/lib/native";
import { toast } from "sonner";
import { useUserWebSocket } from "@/lib/useUserWebSocket";
import {
  IdentificationBadgeIcon,
  UploadSimpleIcon,
  SealCheckIcon,
  WarningIcon,
  ClockCountdownIcon,
  CameraIcon,
} from "@phosphor-icons/react";

export default function Verify() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }
    setStatus(user.verification_status);
  }, [user, navigate]);

  // Real-time push: the moment an admin approves or rejects THIS
  // user's ID, flip the status card without needing a manual refresh.
  useUserWebSocket({
    enabled: !!user,
    onEvent: async (msg) => {
      if (msg.type !== "verification.decision") return;
      if (msg.user_id && user && msg.user_id !== user.user_id) return;
      setStatus(msg.status);
      try { await refreshUser(); } catch (_) { /* ignore */ }
      if (msg.status === "approved") {
        toast.success("You're verified — welcome to Bidorah!");
      } else if (msg.status === "rejected") {
        toast.error(msg.reason || "Verification needs another try.");
      }
    },
  });

  const upload = async () => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      await api.post("/verification/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Document submitted. Our team will review it shortly.");
      await refreshUser();
      setStatus("pending");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setUploading(false);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="verify-page">
      <Navbar />
      <main className="flex-1 max-w-3xl mx-auto w-full px-6 py-12">
        <p className="overline text-zinc-500 mb-2">ID VERIFICATION</p>
        <h1 className="font-display font-black text-4xl lg:text-5xl tracking-tighter">
          Verify your identity
        </h1>
        <p className="text-zinc-600 mt-3">
          Bidorah requires every member to verify with a US government-issued photo ID
          before bidding, purchasing, or listing. This keeps sellers honest and buyers protected.
        </p>

        <div className="mt-10 border border-zinc-200 bg-white">
          <div className="p-6 border-b border-zinc-200 flex items-center gap-4">
            <div className="h-12 w-12 grid place-items-center bg-[#1D78AB] text-white">
              <IdentificationBadgeIcon size={24} />
            </div>
            <div>
              <div className="font-display font-bold text-lg">Your verification status</div>
              <StatusChip status={status} />
            </div>
          </div>
          <div className="p-6 space-y-4">
            {status === "approved" ? (
              <div className="flex items-center gap-3 p-4 border border-emerald-300 bg-emerald-50">
                <SealCheckIcon size={22} className="text-emerald-600" />
                <p className="text-sm">
                  You're verified. You can now list, bid, and buy on Bidorah.
                </p>
              </div>
            ) : status === "pending" ? (
              <div className="flex items-center gap-3 p-4 border border-blue-300 bg-blue-50">
                <ClockCountdownIcon size={22} className="text-[#1D78AB]" />
                <p className="text-sm">
                  Your document was received. We'll review it within 1 business day. You can re-upload below if needed.
                </p>
              </div>
            ) : status === "rejected" ? (
              <div className="flex items-start gap-3 p-4 border border-red-300 bg-red-50">
                <WarningIcon size={22} className="text-red-600 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold">Your submission was rejected.</p>
                  {user.rejection_reason && (
                    <p className="text-sm mt-1 text-zinc-700">{user.rejection_reason}</p>
                  )}
                  <p className="text-sm mt-1 text-zinc-700">Please upload a clearer photo below.</p>
                </div>
              </div>
            ) : null}

            <div className="overline text-zinc-500 pt-2">Accepted documents</div>
            <ul className="text-sm text-zinc-600 list-disc ml-5 space-y-1">
              <li>US Driver's License</li>
              <li>US Passport or Passport Card</li>
              <li>US State-Issued ID</li>
              <li>US Military ID</li>
            </ul>

            <label
              htmlFor="id-file"
              className="block border-2 border-dashed border-zinc-300 p-8 text-center cursor-pointer hover:border-[#1D78AB] transition-colors"
              data-testid="id-upload-dropzone"
            >
              <UploadSimpleIcon size={32} className="mx-auto text-zinc-400 mb-2" />
              <p className="font-semibold text-sm">
                {file ? file.name : "Click to upload a photo of your ID"}
              </p>
              <p className="text-xs text-zinc-500 mt-1">JPG, PNG or PDF. Max 8MB.</p>
              <input
                id="id-file"
                type="file"
                accept="image/*,.pdf"
                className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                data-testid="id-file-input"
              />
            </label>

            {isNative() && (
              <Button
                type="button"
                variant="outline"
                onClick={async () => {
                  try {
                    const captured = await takePhoto();
                    setFile(captured);
                  } catch (e) {
                    toast.error("Couldn't open the camera");
                  }
                }}
                className="w-full h-11 border-zinc-300"
                data-testid="id-camera-btn"
              >
                <CameraIcon size={18} className="mr-2" /> Take photo with camera
              </Button>
            )}

            <Button
              onClick={upload}
              disabled={!file || uploading}
              className="w-full bg-[#1D78AB] hover:bg-[#165d85] text-white h-11"
              data-testid="submit-id-btn"
            >
              {uploading ? "Uploading..." : "Submit for review"}
            </Button>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function StatusChip({ status }) {
  const map = {
    unsubmitted: { text: "Not submitted", cls: "bg-zinc-100 text-zinc-800" },
    pending: { text: "Under review", cls: "bg-blue-100 text-[#165d85]" },
    approved: { text: "Approved", cls: "bg-emerald-100 text-emerald-700" },
    rejected: { text: "Rejected", cls: "bg-red-100 text-red-700" },
  };
  const s = map[status] || map.unsubmitted;
  return (
    <span
      className={`inline-block mt-1 px-2 py-0.5 text-[10px] font-bold tracking-widest uppercase ${s.cls}`}
      data-testid="verification-status-chip"
    >
      {s.text}
    </span>
  );
}
